import { Link, useLocation, useParams } from 'react-router';

import { ChevronLeftIcon } from '@radix-ui/react-icons';
import { useQuery } from '@tanstack/react-query';

import { SESSION_STORAGE_KEYS } from '@/lib/constants/sessionStorageKeys';

import { glacierPolicyApiGetPolicyOptions } from '@/lib/heyapi/@tanstack/react-query.gen';
import type { Policy, PolicyDetails } from '@/lib/heyapi/types.gen';
import {
  InsuranceLinesEnum,
  PlSubTypeEnum,
  TransactionStatus,
  TransactionType,
  UsStateEnum,
} from '@/lib/heyapi/types.gen';

import { Profile } from '../components/auth/LoggedInUser';
import { usePermissionsStore } from '../stores/usePermissionsStore';

interface HideIfParams {
  selectedLines: Array<InsuranceLinesEnum>;
  subtype: PlSubTypeEnum | null | undefined;
  latestTransaction: Policy['latestTransaction'] | undefined;
  scheduleFile?: string | null;
  policyDetails?: PolicyDetails | undefined;
}

type NavRoute = {
  to: string;
  description: string;
  icon?: React.JSX.Element | string;
  hideIf: (params: HideIfParams) => boolean;
};

/**
 * Use to define the links for navigation in the app
 * https://reactrouter.com/en/6.24.1/components/link
 */
const ROUTES: Array<NavRoute> = [
  {
    to: 'pricing-preview',
    description: 'Midterm: Pricing Preview',
    icon: '',
    hideIf({ latestTransaction }) {
      return !(
        latestTransaction?.transactionType !== TransactionType.POLICY_BOUND &&
        latestTransaction?.status === TransactionStatus.IN_PROGRESS
      );
    },
  },
  {
    to: 'erp',
    description: 'Midterm: Extended Reporting Period',
    icon: '',
    hideIf({ latestTransaction }) {
      return !(
        latestTransaction?.transactionType === TransactionType.ERP &&
        latestTransaction?.status === TransactionStatus.IN_PROGRESS
      );
    },
  },
  {
    to: 'policy-details',
    description: 'Policy Details',
    icon: '',
    hideIf() {
      return false;
    },
  },
  {
    to: 'practice-locations',
    description: 'Practice Locations',
    icon: '',
    hideIf() {
      return false;
    },
  },
  {
    to: 'common',
    description: 'Policy Common Forms',
    icon: '',
    hideIf() {
      return false;
    },
  },

  {
    to: 'prof-liability-coverage',
    description: 'Professional Liability Coverage',
    icon: '',
    hideIf({ selectedLines }) {
      return !selectedLines.includes(InsuranceLinesEnum.PL);
    },
  },
  {
    to: 'prof-liability-dentist',
    description: 'Professional Liability Dentists',
    icon: '',
    hideIf({ selectedLines, subtype, scheduleFile }) {
      return (
        !selectedLines.includes(InsuranceLinesEnum.PL) ||
        subtype === PlSubTypeEnum.DENTAL_ASSOCIATION ||
        subtype === PlSubTypeEnum.TESTING_BOARD ||
        scheduleFile === 'entity_only'
      );
    },
  },
  {
    to: 'prof-liability-practice-owners',
    description: 'Professional Liability Practice Owners',
    icon: '',
    hideIf({ selectedLines, subtype }) {
      return (
        !selectedLines.includes(InsuranceLinesEnum.PL) ||
        subtype === PlSubTypeEnum.VOLUNTEER_ONLY ||
        subtype === PlSubTypeEnum.FACULTY_ONLY ||
        subtype === PlSubTypeEnum.TESTING_BOARD ||
        subtype === PlSubTypeEnum.DENTAL_ASSOCIATION
      );
    },
  },
  {
    to: 'owned-entities',
    description: 'Owned Entities',
    icon: '',
    hideIf() {
      return false;
    },
  },
  {
    to: 'cyber-liability',
    description: 'Cyber Liability',
    icon: '',
    hideIf({ selectedLines, policyDetails }) {
      return (
        !selectedLines.includes(InsuranceLinesEnum.CYBER) ||
        policyDetails?.policyContractState !== UsStateEnum.CA
      );
    },
  },
  {
    to: 'epli',
    description: 'Employment Practices Liability Coverage',
    icon: '',

    hideIf({ selectedLines, subtype }) {
      return (
        !selectedLines.includes(InsuranceLinesEnum.EPLI) ||
        subtype === PlSubTypeEnum.VOLUNTEER_ONLY
      );
    },
  },
  {
    to: 'general-liability',
    description: 'General Liability Coverage',
    icon: '',

    hideIf({ selectedLines, subtype }) {
      return (
        !selectedLines.includes(InsuranceLinesEnum.GL) ||
        subtype === PlSubTypeEnum.VOLUNTEER_ONLY ||
        subtype === PlSubTypeEnum.FACULTY_ONLY ||
        subtype === PlSubTypeEnum.NEW_GRAD
      );
    },
  },
  {
    to: 'erisa-fiduciary-and-employee-benefits',
    description: 'ERISA Fiduciary & Employee Benefits Liability Coverage',
    icon: '',

    hideIf({ selectedLines, subtype }) {
      return (
        !selectedLines.includes(InsuranceLinesEnum.ERISA) ||
        subtype === PlSubTypeEnum.VOLUNTEER_ONLY ||
        subtype === PlSubTypeEnum.FACULTY_ONLY ||
        subtype === PlSubTypeEnum.NEW_GRAD
      );
    },
  },

  {
    to: 'commercial-property-locations',
    description: 'Commercial Property Location Coverages',
    icon: '',

    hideIf({ selectedLines, subtype }) {
      return (
        !selectedLines.includes(InsuranceLinesEnum.CP) ||
        subtype === PlSubTypeEnum.VOLUNTEER_ONLY ||
        subtype === PlSubTypeEnum.FACULTY_ONLY ||
        subtype === PlSubTypeEnum.NEW_GRAD
      );
    },
  },
  {
    to: 'commercial-property-coverages',
    description: 'Commercial Property Policy Coverages',
    icon: '',
    hideIf({ selectedLines, subtype }) {
      return (
        !selectedLines.includes(InsuranceLinesEnum.CP) ||
        subtype === PlSubTypeEnum.VOLUNTEER_ONLY ||
        subtype === PlSubTypeEnum.FACULTY_ONLY ||
        subtype === PlSubTypeEnum.NEW_GRAD
      );
    },
  },

  {
    to: 'policy-decisions',
    description: 'Policy Decisions',
    icon: '',
    hideIf() {
      return false;
    },
  },
  {
    to: 'document-warehouse',
    description: 'Document Warehouse',
    icon: '',
    hideIf() {
      return false;
    },
  },
  {
    to: 'claims',
    description: 'Claims',
    icon: '',

    hideIf() {
      return false;
    },
  },
  {
    to: 'transactions',
    description: 'Transactions',
    icon: '',

    hideIf() {
      return false;
    },
  },
];

const NavigationView = ({
  selectedLines,
  subtype,
  latestTransaction,
  scheduleFile,
  policyDetails,
}: {
  selectedLines: Array<InsuranceLinesEnum>;
  subtype: PlSubTypeEnum | undefined | null;
  latestTransaction: Policy['latestTransaction'] | undefined;
  scheduleFile?: string | null;
  policyDetails: PolicyDetails | undefined;
}) => {
  const location = useLocation();
  const isTimeTraveling = usePermissionsStore((state) => state.isTimeTraveling);
  const originHomePath = sessionStorage.getItem('originalHomePath') ?? '/';

  const env = import.meta.env.VITE_SENTRY_ENV;

  const showBadge = !env?.startsWith('prod');

  const getBadgeClass = () => {
    if (env === 'staging') {
      return 'bg-yellow-700';
    }

    return 'bg-green-700';
  };

  const getBackToText = (originHomePath: string | undefined) => {
    if (originHomePath === '/search') {
      return 'Back to Search';
    }
    return 'Back to My Work Queue';
  };

  const getBackToURL = (originHomePath: string | undefined) => {
    if (originHomePath === '/search') {
      return '/search';
    }
    return '/';
  };

  return (
    <aside className="text-0 flex h-screen w-full flex-col border-r bg-brand-dark-green">
      <div className="flex h-full flex-1 flex-col overflow-hidden">
        <div className="flex flex-shrink-0 items-center border-0 border-b border-b-[#ffffff19]">
          <Link to="/" key="Home" className="my-4 ml-[16px] flex">
            <img src="/assets/aspen_sm.svg" alt="Aspen logo" className="w-24" />
          </Link>
          {showBadge ? (
            <div
              className={`${getBadgeClass()} ml-16 rounded p-1 font-extrabold text-white`}
            >
              {import.meta.env.VITE_SENTRY_ENV}
            </div>
          ) : null}
        </div>
        <nav className="flex min-h-0 flex-1 flex-col sm:pt-4">
          <div className="flex h-full min-h-0 flex-col">
            <div className="mb-4 flex-shrink-0 border-0 border-b border-b-[#ffffff19] pb-4">
              <Link
                to={getBackToURL(originHomePath)}
                key="back"
                className="flex w-full cursor-pointer items-center justify-start px-[6px] py-[10px] text-sm !text-brand-300 hover:bg-transparent hover:!text-brand-300"
                onClick={(e) => {
                  if (isTimeTraveling) {
                    e.preventDefault();
                    sessionStorage.removeItem(SESSION_STORAGE_KEYS.SEQUENCE_ID);
                    window.location.href = getBackToURL(originHomePath);
                  }
                }}
              >
                <ChevronLeftIcon className="mr-[4px]" />
                {getBackToText(originHomePath)}
              </Link>
            </div>
            <div
              className="sidebar-scroll min-h-0 flex-1 overflow-y-auto px-2 pb-4"
              style={{
                width: 'calc(100% - 2px)',
              }}
            >
              {ROUTES.map(({ to, description, icon, hideIf }) => {
                if (
                  hideIf({
                    selectedLines,
                    subtype,
                    latestTransaction,
                    scheduleFile,
                    policyDetails,
                  })
                ) {
                  return null;
                }
                const active = location.pathname.includes(to);
                const cn = `group flex px-[6px] py-[12px] text-sm font-semibold text-brand-0 ${active ? 'rounded-[8px] bg-brand-0 text-brand-700' : ''}`;

                return (
                  <Link key={description} to={to} className={cn}>
                    {icon}
                    {description}
                  </Link>
                );
              })}
            </div>
          </div>
          <div className="flex-shrink-0">
            <Profile />
          </div>
          <div className="mb-4 mt-auto flex flex-shrink-0 justify-center">
            <img
              src="/assets/powered_by_counterpart.png"
              alt="Powered by Counterpart"
              className="w-[64%]"
            />
          </div>
        </nav>
      </div>
    </aside>
  );
};

const Navigation = () => {
  const { id } = useParams();

  const { data } = useQuery({
    ...glacierPolicyApiGetPolicyOptions({
      path: {
        account_id: id!,
      },
    }),
  });

  return (
    <NavigationView
      selectedLines={data?.details?.insuranceLinesDesired || []}
      subtype={data?.details?.plSubType}
      latestTransaction={data?.latestTransaction}
      scheduleFile={data?.details?.scheduleFile}
      policyDetails={data?.details}
    />
  );
};

export default Navigation;
